import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { TabBar } from 'antd-mobile';
import { AppOutline, CouponOutline, UserSetOutline } from 'antd-mobile-icons';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { customerServiceApi, ApiEnvelope, CustomerServiceConversation } from '../api/customer-service';
import { useAuthStore } from '../stores/auth.store';

// Tab definition is kept as a static structure; the user-facing title is
// resolved via i18n at render time (see `tabs` memo inside the component).
const TAB_DEFS = [
  { key: '/', icon: <AppOutline />, i18nKey: 'layout.tabs.dashboard' },
  { key: '/manual-activity', icon: <CouponOutline />, i18nKey: 'layout.tabs.manualActivity' },
  { key: '/profile', icon: <UserSetOutline />, i18nKey: 'layout.tabs.profile' },
];

const FLOAT_STORAGE_KEY = 'cmp-webview-service-float';
const FLOAT_MARGIN = 14;
const FLOAT_TOP_OFFSET = 78;
const FLOAT_TABBAR_OFFSET = 104;
const FLOAT_EXPANDED_WIDTH = 54;
const FLOAT_EXPANDED_HEIGHT = 54;
const FLOAT_COMPACT_SIZE = 54;
const UNREAD_POLL_INTERVAL_MS = 8000;

interface ServiceFloatState {
  x: number;
  y: number;
  compact: boolean;
}

export const MainLayout: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation('common');
  const accessToken = useAuthStore((state) => state.accessToken);
  // Resolve tab titles each render so language changes propagate.
  const tabs = useMemo(
    () => TAB_DEFS.map((item) => ({ ...item, title: t(item.i18nKey) })),
    [t]
  );
  const dragStateRef = useRef({
    pointerId: -1,
    startX: 0,
    startY: 0,
    originX: 0,
    originY: 0,
    moved: false,
  });
  const [floatState, setFloatState] = useState<ServiceFloatState>(() => getDefaultFloatState());
  const [isDragging, setIsDragging] = useState(false);
  const [serviceUnreadCount, setServiceUnreadCount] = useState(0);

  const activeKey = tabs.find((item) => item.key !== '/' && location.pathname.startsWith(item.key))?.key
    || (location.pathname === '/' ? '/' : '');
  const isCustomerServicePage = location.pathname.startsWith('/customer-service');

  const syncServiceUnreadCount = useCallback(async () => {
    if (!accessToken) {
      setServiceUnreadCount(0);
      return;
    }

    if (isCustomerServicePage) {
      setServiceUnreadCount(0);
      return;
    }

    try {
      const response = await customerServiceApi.getMyConversation() as { data: ApiEnvelope<CustomerServiceConversation> };
      setServiceUnreadCount(Math.max(response.data.data.unread_count || 0, 0));
    } catch {
      // Keep the current badge if the lightweight sync fails.
    }
  }, [accessToken, isCustomerServicePage]);

  const updateFloatState = useCallback((nextState: ServiceFloatState) => {
    setFloatState(nextState);
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(FLOAT_STORAGE_KEY, JSON.stringify(nextState));
    }
  }, []);

  // Poll only while the page is visible to reduce unnecessary mobile traffic.
  useEffect(() => {
    if (!accessToken) {
      setServiceUnreadCount(0);
      return;
    }

    let cancelled = false;
    let pollTimer: ReturnType<typeof setInterval> | null = null;

    const isPageVisible = () => (
      typeof document === 'undefined' || document.visibilityState === 'visible'
    );

    const clearPollTimer = () => {
      if (pollTimer) {
        clearInterval(pollTimer);
        pollTimer = null;
      }
    };

    const runSync = () => {
      if (cancelled) {
        return;
      }

      if (!isPageVisible()) {
        return;
      }

      void syncServiceUnreadCount();
    };

    const startPolling = () => {
      clearPollTimer();
      if (!isPageVisible() || isCustomerServicePage) {
        return;
      }

      pollTimer = setInterval(runSync, UNREAD_POLL_INTERVAL_MS);
    };

    const handleVisibilityChange = () => {
      if (!isPageVisible()) {
        clearPollTimer();
        return;
      }

      runSync();
      startPolling();
    };

    runSync();
    startPolling();
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      cancelled = true;
      clearPollTimer();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [accessToken, isCustomerServicePage, syncServiceUnreadCount]);

  // Sync once on every in-app route change.
  useEffect(() => {
    if (!accessToken) {
      return;
    }

    void syncServiceUnreadCount();
  }, [location.pathname, accessToken, syncServiceUnreadCount]);

  // Mark messages as read when entering the customer service page.
  useEffect(() => {
    if (!isCustomerServicePage || !accessToken) {
      return;
    }

    const markReadOnEnter = async () => {
      try {
        const response = await customerServiceApi.getMyConversation() as { data: ApiEnvelope<CustomerServiceConversation> };
        if (response.data.data.id) {
          await customerServiceApi.markRead(response.data.data.id);
        }
        setServiceUnreadCount(0);
      } catch {
        // Ignore failures to avoid blocking chat entry.
      }
    };

    void markReadOnEnter();
  }, [isCustomerServicePage, accessToken]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const storedState = window.localStorage.getItem(FLOAT_STORAGE_KEY);
    if (!storedState) {
      updateFloatState(getDefaultFloatState());
      return;
    }

    try {
      const parsed = JSON.parse(storedState) as Partial<ServiceFloatState>;
      const mergedState = clampFloatState({
        x: typeof parsed.x === 'number' ? parsed.x : getDefaultFloatState().x,
        y: typeof parsed.y === 'number' ? parsed.y : getDefaultFloatState().y,
        compact: typeof parsed.compact === 'boolean' ? parsed.compact : false,
      });
      setFloatState(mergedState);
    } catch {
      updateFloatState(getDefaultFloatState());
    }
  }, [updateFloatState]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const handleResize = () => {
      updateFloatState(clampFloatState(floatState));
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [floatState, updateFloatState]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const handlePointerMove = (event: PointerEvent) => {
      if (dragStateRef.current.pointerId !== event.pointerId) {
        return;
      }

      const deltaX = event.clientX - dragStateRef.current.startX;
      const deltaY = event.clientY - dragStateRef.current.startY;
      if (!dragStateRef.current.moved && Math.abs(deltaX) + Math.abs(deltaY) > 6) {
        dragStateRef.current.moved = true;
        setIsDragging(true);
      }

      const nextState = clampFloatState({
        x: dragStateRef.current.originX + deltaX,
        y: dragStateRef.current.originY + deltaY,
        compact: false,
      });
      setFloatState(nextState);
    };

    const handlePointerUp = (event: PointerEvent) => {
      if (dragStateRef.current.pointerId !== event.pointerId) {
        return;
      }

      const moved = dragStateRef.current.moved;
      dragStateRef.current.pointerId = -1;
      dragStateRef.current.moved = false;
      setIsDragging(false);

      if (!moved) {
        navigate('/customer-service');
        return;
      }

      const snappedState = snapFloatStateToRightEdge(floatState);
      updateFloatState(snappedState);
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);
    window.addEventListener('pointercancel', handlePointerUp);
    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
      window.removeEventListener('pointercancel', handlePointerUp);
    };
  }, [floatState, navigate, updateFloatState]);

  const floatStyle = useMemo(() => ({
    transform: `translate3d(${floatState.x}px, ${floatState.y}px, 0)`,
  }), [floatState.x, floatState.y]);

  const handleServicePointerDown = (event: React.PointerEvent<HTMLButtonElement>) => {
    dragStateRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      originX: floatState.x,
      originY: floatState.y,
      moved: false,
    };
  };

  return (
    <div className="webview-shell">
      {!isCustomerServicePage && (
        <button
          type="button"
          className={`webview-service-float${floatState.compact ? ' is-compact' : ''}${isCustomerServicePage ? ' is-active' : ''}${isDragging ? ' is-dragging' : ''}`}
          style={floatStyle}
          onPointerDown={handleServicePointerDown}
        >
          <span className="webview-service-float-icon">
            <span className="webview-service-float-core">
              <CustomerServiceGlyph />
            </span>
            {serviceUnreadCount > 0 ? (
              <span className="webview-service-float-badge">{formatUnreadCount(serviceUnreadCount)}</span>
            ) : null}
          </span>
          <span className="webview-service-float-text">{t('layout.customerService')}</span>
        </button>
      )}
      <Outlet />
      {!isCustomerServicePage && (
        <div className="webview-floating-tabbar">
          <TabBar activeKey={activeKey} onChange={(value) => navigate(value)}>
            {tabs.map((item) => (
              <TabBar.Item key={item.key} icon={item.icon} title={item.title} />
            ))}
          </TabBar>
        </div>
      )}
    </div>
  );
};

function getDefaultFloatState(): ServiceFloatState {
  if (typeof window === 'undefined') {
    return { x: 300, y: 520, compact: false };
  }

  const bounds = getFloatBounds(false);
  return {
    x: bounds.maxX,
    y: bounds.maxY,
    compact: false,
  };
}

function formatUnreadCount(count: number): string {
  if (count > 99) {
    return '99+';
  }

  return String(count);
}

function getFloatBounds(compact: boolean): { minX: number; maxX: number; minY: number; maxY: number } {
  const viewportWidth = typeof window === 'undefined' ? 430 : window.innerWidth;
  const viewportHeight = typeof window === 'undefined' ? 900 : window.innerHeight;
  const shellWidth = Math.min(430, viewportWidth);
  const shellLeft = Math.max((viewportWidth - shellWidth) / 2, 0);
  const width = compact ? FLOAT_COMPACT_SIZE : FLOAT_EXPANDED_WIDTH;
  const height = compact ? FLOAT_COMPACT_SIZE : FLOAT_EXPANDED_HEIGHT;
  const minX = shellLeft + FLOAT_MARGIN;
  const maxX = shellLeft + shellWidth - width - FLOAT_MARGIN;
  const minY = FLOAT_TOP_OFFSET;
  const maxY = viewportHeight - FLOAT_TABBAR_OFFSET - height;

  return {
    minX,
    maxX: Math.max(minX, maxX),
    minY,
    maxY: Math.max(minY, maxY),
  };
}

function clampFloatState(state: ServiceFloatState): ServiceFloatState {
  const bounds = getFloatBounds(state.compact);
  return {
    ...state,
    x: Math.min(Math.max(state.x, bounds.minX), bounds.maxX),
    y: Math.min(Math.max(state.y, bounds.minY), bounds.maxY),
  };
}

function snapFloatStateToRightEdge(state: ServiceFloatState): ServiceFloatState {
  const bounds = getFloatBounds(true);
  return {
    compact: true,
    x: bounds.maxX,
    y: Math.min(Math.max(state.y, bounds.minY), bounds.maxY),
  };
}

const CustomerServiceGlyph: React.FC = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path
      d="M7 11.75V10.75C7 8.1005 9.23858 5.95 12 5.95C14.7614 5.95 17 8.1005 17 10.75V11.75"
      stroke="currentColor"
      strokeWidth="1.85"
      strokeLinecap="round"
    />
    <rect
      x="5.3"
      y="11.1"
      width="2.95"
      height="5.8"
      rx="1.475"
      stroke="currentColor"
      strokeWidth="1.85"
    />
    <rect
      x="15.75"
      y="11.1"
      width="2.95"
      height="5.8"
      rx="1.475"
      stroke="currentColor"
      strokeWidth="1.85"
    />
    <path
      d="M17.2 16.6V17C17.2 18.5464 15.9464 19.8 14.4 19.8H12.9"
      stroke="currentColor"
      strokeWidth="1.85"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M10.2 13.2C10.2 12.5925 10.6925 12.1 11.3 12.1H14.7C15.3075 12.1 15.8 12.5925 15.8 13.2V14.55C15.8 15.1575 15.3075 15.65 14.7 15.65H13.35L11.8 16.95C11.4388 17.253 10.9 16.9962 10.9 16.5248V15.65H11.3C10.6925 15.65 10.2 15.1575 10.2 14.55V13.2Z"
      fill="currentColor"
      fillOpacity="0.96"
    />
    <path d="M12.3 18.1H14.1" stroke="currentColor" strokeWidth="1.85" strokeLinecap="round" />
  </svg>
);
